// This optional code is used to register a service worker.
// register() is not called by default.

// This lets the app load faster on subsequent visits in production, and gives
// it offline capabilities. However, it also means that developers (and users)
// will only see deployed updates on subsequent visits to a page, after all the
// existing tabs open on the page have been closed, since previously cached
// resources are updated in the background.

// To learn more about the benefits of this model and instructions on how to
// opt-in, read https://cra.link/PWA

const isLocalhost = Boolean(
  window.location.hostname === 'localhost' ||
    // [::1] is the IPv6 localhost address.
    window.location.hostname === '[::1]' ||
    // 127.0.0.0/8 are considered localhost for IPv4.
    window.location.hostname.match(/^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/)
);

export function register(config) {
  if (process.env.NODE_ENV === 'production' && 'serviceWorker' in navigator) {
    // The URL constructor is available in all browsers that support SW.
    const publicUrl = new URL(process.env.PUBLIC_URL, window.location.href);
    if (publicUrl.origin !== window.location.origin) {
      // Our service worker won't work if PUBLIC_URL is on a different origin
      // from what our page is served on. This might happen if a CDN is used to
      // serve assets; see https://github.com/facebook/create-react-app/issues/2374
      return;
    }

    const start = () => {
      const swUrl = `${process.env.PUBLIC_URL}/service-worker.js`;

      if (isLocalhost) {
        // This is running on localhost. Let's check if a service worker still exists or not.
        checkValidServiceWorker(swUrl, config);

        // Add some additional logging to localhost, pointing developers to the
        // service worker/PWA documentation.
        navigator.serviceWorker.ready.then(() => {
          console.log(
            'This web app is being served cache-first by a service ' +
              'worker. To learn more, visit https://cra.link/PWA'
          );
        });
      } else {
        // Is not localhost. Just register service worker
        registerValidSW(swUrl, config);
      }
    };

    // `register()` is called from a React effect, which can run after the load
    // event has already fired. Waiting for a load that will never come would
    // leave the worker unregistered for the whole session (and the update poll
    // with it), so start immediately in that case.
    if (document.readyState === 'complete') {
      start();
    } else {
      window.addEventListener('load', start);
    }
  }
}

// How often to poll for a new worker while the tab is in the foreground.
const UPDATE_CHECK_INTERVAL_MS = 60 * 1000;

// Guards against attaching a second `statechange` listener to the same worker.
// The post-registration check and the `updatefound` event can both observe the
// same install, and a duplicate listener would fire `onUpdate` twice.
const trackedWorkers = new WeakSet();

// A worker that reaches `installed` while another worker is in control is
// waiting to take over - that is the update the user needs telling about.
//
// Read `registration.installing` rather than `registration.waiting`: the
// installing worker is the one we already hold a reference to, whereas
// `registration.waiting` is not guaranteed to be populated at this point.
function trackInstalling(registration, config) {
  const installingWorker = registration.installing;
  if (installingWorker == null || trackedWorkers.has(installingWorker)) {
    return;
  }
  trackedWorkers.add(installingWorker);

  const handleStateChange = () => {
    if (installingWorker.state !== 'installed') {
      return;
    }

    if (navigator.serviceWorker.controller) {
      // The updated content is cached, but the old worker keeps serving until
      // it is told to step aside. `onUpdate` is handed the worker itself so the
      // app can post SKIP_WAITING straight to it.
      if (config && config.onUpdate) {
        config.onUpdate(installingWorker);
      }
    } else if (config && config.onSuccess) {
      // First ever install - everything is precached, nothing to update.
      config.onSuccess(registration);
    }
  };

  installingWorker.addEventListener('statechange', handleStateChange);
  // The worker may have reached `installed` before this listener was attached,
  // in which case no `statechange` event is coming for that transition.
  handleStateChange();
}

function registerValidSW(swUrl, config) {
  navigator.serviceWorker
    // Never let the browser's HTTP cache hide a newly deployed worker. The
    // server also sends no-store for the worker, but this keeps the update
    // check honest if the response is cached anywhere else along the way.
    .register(swUrl, { updateViaCache: 'none' })
    .then((registration) => {
      // Attach before anything else. An install already in flight fires
      // `updatefound` while registration is still settling, and a listener
      // added after that misses it for the lifetime of the page.
      trackInstalling(registration, config);
      registration.addEventListener('updatefound', () => trackInstalling(registration, config));

      const checkForUpdate = () => {
        // A rejection here just means offline or an unreachable server; the
        // next tick retries.
        registration.update().catch(() => {});
      };

      setInterval(() => {
        // Background tabs have their timers throttled or frozen, so an
        // unconditional poll would rarely run for an installed PWA.
        if (document.visibilityState === 'visible') {
          checkForUpdate();
          console.debug('Checked for update...');
        }
      }, UPDATE_CHECK_INTERVAL_MS);

      // An installed PWA is usually resumed rather than navigated to, so the
      // browser's own navigation-triggered check can go a long time without
      // firing. This is what catches a user returning to a long-lived tab.
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
          checkForUpdate();
        }
      });
    })
    .catch((error) => {
      console.error('Error during service worker registration:', error);
    });
}

function checkValidServiceWorker(swUrl, config) {
  // Check if the service worker can be found. If it can't reload the page.
  fetch(swUrl, {
    headers: { 'Service-Worker': 'script' }
  })
    .then((response) => {
      // Ensure service worker exists, and that we really are getting a JS file.
      const contentType = response.headers.get('content-type');
      if (
        response.status === 404 ||
        (contentType != null && contentType.indexOf('javascript') === -1)
      ) {
        // No service worker found. Probably a different app. Reload the page.
        navigator.serviceWorker.ready.then((registration) => {
          registration.unregister().then(() => {
            window.location.reload();
          });
        });
      } else {
        // Service worker found. Proceed as normal.
        registerValidSW(swUrl, config);
      }
    })
    .catch(() => {
      console.log('No internet connection found. App is running in offline mode.');
    });
}

export function unregister() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready
      .then((registration) => {
        registration.unregister();
      })
      .catch((error) => {
        console.error(error.message);
      });
  }
}
